/**
 * CodCashReceiptDialog: a Lusuku agent records cash received from a rider.
 * ───────────────────────────────────────────────────────────────────────────
 * Two ways in, one dialog (mounted once in App.js, opened through
 * utilities/codCashReceiptBus.js):
 *
 *   order  "Mark as Paid" on a cash-on-delivery order. The server refuses to
 *          mark COD paid (409 COD_NEEDS_CASH_RECEIPT) and markOrderAsPaid opens
 *          this instead. The amount is the order total and is not editable.
 *          POST /weborders/:orderId/cod/agent-receipt
 *   rider  "Receive cash" on the Riders page, for a rider who could not deposit
 *          by MTN. The agent types how much they received, up to what the rider
 *          holds. POST /api/riders/cod/:riderId/agent-receipt
 *
 * The agent then says where the cash went: paid into the Lusuku MTN
 * collections account (with the MTN transaction id, which finance reconciles
 * against the MTN statement) or kept as cash at hand, booked to the agent by
 * name. The rider is cleared the moment it saves.
 *
 * A receipt key is minted each time the dialog opens, so a double click or a
 * retry after a timeout records the cash once. The server holds every rule;
 * the checks here only stop an obviously incomplete submit.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import ConfirmDialog from "./ConfirmDialog";
import { subscribeCodCashReceipt } from "../utilities/codCashReceiptBus";
import { apiRecordOrderCashReceipt, apiReceiveRiderCash } from "../api/index.js";
import { MARK_ORDER_PAID } from "../constants/webOrderConstants";

const ugx = (n) => `UGX ${Math.round(Number(n) || 0).toLocaleString()}`;

const newReceiptKey = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (_) {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const CHANNELS = [
  {
    value: "AGENT_MTN_DEPOSIT",
    title: "Deposited to Lusuku MTN",
    hint: "You paid the cash into the Lusuku MTN collections account.",
  },
  {
    value: "AGENT_CASH_AT_HAND",
    title: "Cash at hand",
    hint: "You are keeping the cash. It is recorded against your name.",
  },
];

const styles = {
  summary: { marginBottom: 14 },
  tiles: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 },
  tile: (active) => ({
    display: "block",
    margin: 0,
    padding: "10px 12px",
    borderRadius: 8,
    border: `2px solid ${active ? "#17385a" : "#dde3ea"}`,
    background: active ? "#eef3f8" : "#fff",
    cursor: "pointer",
  }),
  tileTitle: { fontWeight: 600, fontSize: 14 },
  tileHint: { fontSize: 12, color: "#5b6573", marginTop: 2 },
  label: { fontSize: 13, fontWeight: 600, marginBottom: 4 },
};

const CodCashReceiptDialog = () => {
  const dispatch = useDispatch();
  const [request, setRequest] = useState(null);
  const [receiptKey, setReceiptKey] = useState("");
  const [amount, setAmount] = useState("");
  const [channel, setChannel] = useState("");
  const [mtnTransactionId, setMtnTransactionId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(
    () =>
      subscribeCodCashReceipt((next) => {
        setRequest(next);
        setReceiptKey(newReceiptKey());
        setAmount(next?.mode === "rider" ? String(Math.round(Number(next.heldBalance) || 0)) : "");
        setChannel("");
        setMtnTransactionId("");
        setNote("");
      }),
    []
  );

  const isRider = request?.mode === "rider";
  const amountValue = isRider ? Math.round(Number(amount) || 0) : Math.round(Number(request?.amount) || 0);
  const heldBalance = Math.round(Number(request?.heldBalance) || 0);

  const problem = useMemo(() => {
    if (!request) return "closed";
    if (isRider && (amountValue <= 0 || amountValue > heldBalance)) return "amount";
    if (!channel) return "channel";
    if (channel === "AGENT_MTN_DEPOSIT" && mtnTransactionId.trim().length < 6) return "mtn";
    return null;
  }, [request, isRider, amountValue, heldBalance, channel, mtnTransactionId]);

  const close = () => {
    if (!busy) setRequest(null);
  };

  const submit = async () => {
    if (problem) return;
    setBusy(true);
    const body = {
      channel,
      mtnTransactionId: channel === "AGENT_MTN_DEPOSIT" ? mtnTransactionId.trim() : undefined,
      note: note.trim() || undefined,
    };
    try {
      if (isRider) {
        const { data } = await apiReceiveRiderCash(request.riderId, { ...body, amount: amountValue, receiptKey });
        toast.success(data?.msg || "Cash recorded.");
      } else {
        const { data } = await apiRecordOrderCashReceipt(request.orderId, body);
        dispatch({ type: MARK_ORDER_PAID, payload: data });
        toast.success(data?.message || "Cash recorded. The order is paid.");
      }
      request.onDone?.();
      setRequest(null);
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.error || "Could not record the cash. Try again.";
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const body = request ? (
    <div>
      <p style={styles.summary}>
        {isRider ? (
          <>
            <strong>{request.riderName || "This rider"}</strong> holds <strong>{ugx(heldBalance)}</strong> in
            cash from deliveries.
          </>
        ) : (
          <>
            Receive <strong>{ugx(amountValue)}</strong> for this cash-on-delivery order from the rider who
            delivered it.
          </>
        )}
      </p>

      {isRider && (
        <Form.Group style={{ marginBottom: 14 }}>
          <div style={styles.label}>Amount received (UGX)</div>
          <Form.Control
            type="number"
            min={1}
            max={heldBalance}
            value={amount}
            disabled={busy}
            isInvalid={problem === "amount" && amount !== ""}
            onChange={(e) => setAmount(e.target.value)}
          />
          <Form.Control.Feedback type="invalid">
            Enter an amount from 1 to {ugx(heldBalance)}.
          </Form.Control.Feedback>
        </Form.Group>
      )}

      <div style={styles.label}>Where did the cash go?</div>
      <div style={styles.tiles} role="radiogroup">
        {CHANNELS.map((c) => (
          <label key={c.value} style={styles.tile(channel === c.value)}>
            <input
              type="radio"
              name="cod-cash-channel"
              value={c.value}
              checked={channel === c.value}
              disabled={busy}
              onChange={() => setChannel(c.value)}
              style={{ marginRight: 6 }}
            />
            <span style={styles.tileTitle}>{c.title}</span>
            <div style={styles.tileHint}>{c.hint}</div>
          </label>
        ))}
      </div>

      {channel === "AGENT_MTN_DEPOSIT" && (
        <Form.Group style={{ marginBottom: 14 }}>
          <div style={styles.label}>MTN transaction id</div>
          <Form.Control
            type="text"
            value={mtnTransactionId}
            disabled={busy}
            placeholder="From the MTN confirmation message"
            onChange={(e) => setMtnTransactionId(e.target.value)}
          />
        </Form.Group>
      )}

      <Form.Group>
        <div style={styles.label}>Note (optional)</div>
        <Form.Control
          as="textarea"
          rows={2}
          maxLength={500}
          value={note}
          disabled={busy}
          onChange={(e) => setNote(e.target.value)}
        />
      </Form.Group>
    </div>
  ) : null;

  return (
    <ConfirmDialog
      show={!!request}
      tone="primary"
      title={isRider ? "Receive cash from rider" : "Record cash received"}
      body={body}
      note={
        isRider
          ? "The rider can take orders again once what they hold is below their limit."
          : "This closes the order: vendor settlement and the commission invoice run next."
      }
      confirmLabel="Record cash"
      busyLabel="Recording"
      busy={busy}
      confirmDisabled={!!problem}
      onConfirm={submit}
      onCancel={close}
    />
  );
};

export default CodCashReceiptDialog;
